import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';

import { Metamodel } from 'app/model/base/model';
import { FileTypeDatasetAdapter, formatAdapts, compressionAdapts } from './file-type.dataset.adapter';
import { FileShareDataset } from 'app/model/dataset/file-share.dataset';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Owned } from 'app/model/base/named';
import { SftpServerLinkedService } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(FileShareDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            structure: '*',
            linkedServiceName: '*',
            parameters: '*',
            type: '*',
            typeProperties: {
                folderPath: '*',
                fileName: '*',
                fileFilter: '*',
                modifiedDatetimeStart: '*',
                modifiedDatetimeEnd: '*',
                wildcardPath: '*',
                format: formatAdapts,
                compression: compressionAdapts
            }
        }
    }
})
export class FileShareDatasetAdapter extends FileTypeDatasetAdapter<FileShareDataset, Model.DatasetResource> {
    protected async _toResource(dataset: FileShareDataset): Promise<Model.DatasetResource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetResource;
        const modelDataset = this.properties(modelDatasetResource) as Model.FileShareDataset;
        modelDataset.type = Metamodel.modelFor(FileShareDataset).name;
        modelDataset.structure = this.columnValueToResource(dataset, dataset.columns);
        modelDataset.typeProperties = {
            ...this.formatAndCompressionValueToResource(dataset.format, dataset.compression),
            fileName: (ValueAdapter.toResource(dataset, FileShareDataset.FILE_NAME)),
            folderPath: (ValueAdapter.toResource(dataset, FileShareDataset.FOLDER_PATH)),
            fileFilter: (ValueAdapter.toResource(dataset, FileShareDataset.FILE_FILTER)),
            wildcardPath: (ValueAdapter.toResource(dataset, FileShareDataset.WILDCARD_PATH)),
            modifiedDatetimeStart: ValueAdapter.toResource(dataset, FileShareDataset.MODIFID_DATETIME_START),
            modifiedDatetimeEnd: ValueAdapter.toResource(dataset, FileShareDataset.MODIFID_DATETIME_END)
        };
        // SFTP need to append '/' to folderPath
        if (dataset.linkedService instanceof SftpServerLinkedService) {
            const folderPath = modelDataset.typeProperties.folderPath;
            if (_.isString(folderPath) && folderPath.charAt(0) !== '/') {
                modelDataset.typeProperties.folderPath = '/' + folderPath;
            }
        }
        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetResource, dataset?: FileShareDataset): Promise<FileShareDataset> {
        if (!dataset) {
            dataset = FileShareDataset.MODEL.create<FileShareDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        const model = modelDatasetResource.properties as Model.FileShareDataset;

        if (model) {
            if (model.typeProperties != null) {
                Owned.own(dataset, 'format', this.formatResourceToValue(model.typeProperties.format));
                dataset.compression = this.compressionResourceToValue(model.typeProperties.compression);
                ValueAdapter.toModel(dataset, FileShareDataset.WILDCARD_PATH, model.typeProperties.wildcardPath);
                ValueAdapter.toModel(dataset, FileShareDataset.FILE_NAME, model.typeProperties.fileName);
                ValueAdapter.toModel(dataset, FileShareDataset.FOLDER_PATH, model.typeProperties.folderPath);
                ValueAdapter.toModel(dataset, FileShareDataset.FILE_FILTER, model.typeProperties.fileFilter);
                ValueAdapter.toModel(dataset, FileShareDataset.MODIFID_DATETIME_START, model.typeProperties.modifiedDatetimeStart);
                ValueAdapter.toModel(dataset, FileShareDataset.MODIFID_DATETIME_END, model.typeProperties.modifiedDatetimeEnd);
            } else {
                this.initializeModel(dataset);
            }
            dataset.columns = this.columnResourceToValue(dataset, model.structure);
        }
        // TODO: add more properties here

        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, FileShareDataset.MODEL));
        return dataset;
    }
}
